import type { ReactNode } from "react";
import {
  createMagicFormGroupStore,
  type MagicFormGroupContextType,
  type MagicFormGroupMember,
} from "../components/magic-form/MagicFormGroup";

/** The parts of a router location the guard compares. */
export interface UnsavedChangesLocation {
  pathname: string;
  search: string;
  hash: string;
}

/** Whether a navigation keeps an editor on screen, so it does not ask. */
export type UnsavedChangesIsSameRoute = (
  from: UnsavedChangesLocation,
  to: UnsavedChangesLocation,
) => boolean;

/** The default: only a change of the pathname leaves an editor. */
export const defaultIsSameRoute: UnsavedChangesIsSameRoute = (from, to) =>
  from.pathname === to.pathname;

/** An editor the unsaved-changes guard knows. */
export interface UnsavedChangesEntry extends MagicFormGroupMember {
  /** The name in the guard's dialog. */
  displayLabel?: ReactNode;
  /** False for an editor without a save: the dialog then offers no Save. */
  canSave: boolean;
  isSameRoute: UnsavedChangesIsSameRoute;
  /** Whether the editor shows a dialog of its own (the conflict modal). */
  showsConflict?: boolean;
}

export type UnsavedChangesStore =
  MagicFormGroupContextType<UnsavedChangesEntry> & {
    /**
     * Calls `listener` on every registration, also when only an entry's
     * details changed, which `subscribe` does not report, and when the first
     * pending write starts or the last one ends.
     */
    subscribeToEntries: (listener: () => void) => () => void;
    /**
     * Sets or (`undefined`) ends a field's write that is still on its way to
     * the form: `flush` passes it on synchronously.
     */
    setPendingWrite: (id: string, flush: (() => void) | undefined) => void;
    hasPendingWrites: () => boolean;
    /**
     * Passes every pending write on. Runs before the dirty state is read:
     * the router's blocker runs inside `navigate`, so it must be synchronous.
     */
    flushPendingWrites: () => void;
  };

/**
 * The store of the guard: the save order and the refusal of invalid members
 * are those of a `MagicFormGroup`. A store with a `parent` is a scope: what
 * registers in it also registers in the parent, so the route blocker and
 * `beforeunload` at the root see the forms of a modal, while the modal's own
 * `confirmLeave` sees only those.
 */
export function createUnsavedChangesStore(
  parent?: UnsavedChangesStore,
): UnsavedChangesStore {
  const store = createMagicFormGroupStore<UnsavedChangesEntry>();
  const entryListeners = new Set<() => void>();
  const pendingWrites = new Map<string, () => void>();
  const notifyEntryListeners = () => {
    for (const listener of entryListeners) listener();
  };
  return {
    ...store,
    register: (entry) => {
      store.register(entry);
      parent?.register(entry);
      notifyEntryListeners();
    },
    unregister: (id) => {
      store.unregister(id);
      parent?.unregister(id);
    },
    subscribeToEntries: (listener) => {
      entryListeners.add(listener);
      return () => {
        entryListeners.delete(listener);
      };
    },
    setPendingWrite: (id, flush) => {
      const hadWrites = pendingWrites.size > 0;
      if (flush) {
        pendingWrites.set(id, flush);
      } else {
        pendingWrites.delete(id);
      }
      parent?.setPendingWrite(id, flush);
      if (hadWrites !== pendingWrites.size > 0) notifyEntryListeners();
    },
    hasPendingWrites: () => pendingWrites.size > 0,
    flushPendingWrites: () => {
      // A copy: a flush ends its own pending write, which edits the map.
      const flushes = Array.from(pendingWrites.values());
      for (const flush of flushes) flush();
    },
  };
}
